import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { ParallaxMedia } from '../shared/ParallaxMedia';
import { founder, salon } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { useCatalog } from '../../contexts/CatalogContext';
import type { ArticleCategory } from '../../core/types/content';
import { plural } from '../../core/format/text';
import { fill } from '../../utils/copy';
import { btnPrimaryLg, chipCls } from '../../ui/styles';
import { heroTitle } from '../../ui/sections';
import { pageActions } from '../../data/pageActions';
import { MentionedProducts } from './MentionedProducts';
import { JournalPodcast } from './JournalPodcast';
import { articlePath, articlesIn, categoriesOf } from './journalRules';

/**
 * Het journal als tijdschrift: kop, het nieuwste artikel groot met beeld en genoemde producten,
 * de rest in een raster (beeld waar dat er is), en de podcast op klei.
 */
export function Journal() {
  const pages = usePages();
  const {
    articles,
    productsByIds
  } = useCatalog();
  const copy = pages.journal;
  const categories = categoriesOf(articles);
  // De categorie staat in het adres (?categorie=…): terug uit een artikel staat dezelfde keuze er nog.
  const [params, setParams] = useSearchParams();
  const asked = params.get('categorie');
  const category: ArticleCategory | null = categories.find(c => c === asked) ?? null;
  const setCategory = (c: ArticleCategory | null) => {
    const next = new URLSearchParams(params);
    if (c) next.set('categorie', c);else next.delete('categorie');
    setParams(next, {
      replace: true,
      preventScrollReset: true
    });
  };
  const linkState = {
    fromJournal: `/journal${params.toString() ? `?${params.toString()}` : ''}`
  };
  const [featured, ...rest] = articlesIn(articles, category);
  const featuredProducts = productsByIds(featured?.productIds ?? []);
  return <>
      <section className="bg-sand" data-id="element-7608" data-mp="36vfhh01e7cme">
        <div className="mx-auto grid max-w-content gap-10 px-6 pb-10 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-14 lg:pt-20" data-id="element-7609" data-mp="36ue7z000yy6s">
          <div className="lg:col-span-8" data-id="element-7610" data-mp="3fvcb7g2n31ob">
            {copy.hero.eyebrow && <p className="text-[14px] text-clay" data-id="element-7611" data-mp="3fw5a5uvcwmvw">{fill(copy.hero.eyebrow)}</p>}
            <h1 className={`mt-4 max-w-3xl lg:mt-5 ${heroTitle}`} data-id="element-7612" data-mp="3kt452fmugxv5">
              <HeadingText heading={copy.hero.heading} data-id="element-7613" data-mp="2n76quufhzep0" />
            </h1>
          </div>
          <div className="lg:col-span-4 lg:self-end" data-id="element-7614" data-mp="2s5i1v8hj3v8t">
            <div className="flex items-center gap-4 border-t border-ink/15 pt-6" data-id="element-7615" data-mp="2s6awxc74kwb4">
              {/* Decoratief: haar naam staat ernaast. */}
              <img src={founder.portrait} alt="" width={56} height={56} className="h-14 w-14 shrink-0 rounded-full object-cover object-top" data-id="element-7616" data-mp="31vb077snudv4" />
              <p className="text-[14px] leading-snug text-muted" data-id="element-7617" data-mp="24fdx2xsltpg8">
                {fill(copy.hero.byline)} <span className="text-ink" data-id="element-7618" data-mp="29e8h232b5k1x">{founder.name}</span>
                <br data-id="element-7619" data-mp="21vhajpayyxtr" />
                {founder.role} · {salon.city}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Artikelen: eerst, want hier komen lezers via Google binnen */}
      <section className="bg-sand pb-20 sm:pb-24 lg:pb-32" aria-labelledby="journal-articles" data-id="element-7620" data-mp="1rws36qgp8vrr">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7621" data-mp="1mz02p81zo1by">
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-ink/15 pt-6" data-id="element-7622" data-mp="1n0ulafpsfhcv">
            <h2 id="journal-articles" className="text-[14px] text-ink" data-id="element-7623" data-mp="1e66hktzv27q6">
              {fill(copy.articlesHeading)}
            </h2>
            <div role="group" aria-label="Kies een categorie" className="-mx-6 flex min-w-0 max-w-[calc(100%+3rem)] gap-2 overflow-x-auto px-6 [scrollbar-width:none] sm:mx-0 sm:max-w-full sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-7624" data-mp="196adyvv2sib5">
              <button type="button" aria-pressed={category === null} onClick={() => setCategory(null)} className={`${chipCls(category === null)} shrink-0`} data-id="element-7625" data-mp="v72a3pnywm0m">
                Alles
              </button>
              {categories.map(c => <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)} className={`${chipCls(category === c)} shrink-0`} data-id="element-7626" data-mp="1ow9qf8fo8mih">
                  {c}
                </button>)}
            </div>
          </div>

          {featured ? <article className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-16" aria-labelledby="featured-title" data-id="element-7627" data-mp="116p9sw6zq1cd">
              <Link to={articlePath(featured)} state={linkState} className="block lg:col-span-7" tabIndex={-1} aria-hidden="true" data-id="element-7628" data-mp="1684ps9ub9hnw">
                {/* Sfeerbeeld: de titel staat ernaast. Zonder eigen beeld het portret, zodat het vlak nooit leeg is.
                    Het ene moment van deze pagina: het beeld beweegt licht binnen zijn kader. */}
                <ParallaxMedia media={{
              image: featured.image || founder.portraitPalm,
              alt: ''
            }} priority sizes="(min-width: 1024px) 55vw, 100vw" className="aspect-[4/3] w-full" data-id="element-7629" data-mp="2qm579ebjxymf" />
              </Link>
              <div className="lg:col-span-5 lg:self-center" data-id="element-7630" data-mp="axzwe5nlbs25">
                <p className="text-[13px] text-clay" data-id="element-7631" data-mp="avdebc32pu5o">
                  {featured.category} · {featured.readTime} · {featured.date}
                </p>
                <h3 id="featured-title" className="mt-3 type-sub text-ink" data-id="element-7632" data-mp="jxwbbfk6hck0">
                  <Link to={articlePath(featured)} state={linkState} className="transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7633" data-mp="ymslbi5sozn9">
                    {featured.title}
                  </Link>
                </h3>
                <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-7634" data-mp="17nz4jyztwok7">{featured.excerpt}</p>
                {/* De hoofdknop van het journal: het nieuwste artikel. */}
                <Link to={articlePath(featured)} state={linkState} className={`group mt-7 ${btnPrimaryLg}`} data-id="element-7635" data-mp="39eu1wv9oovt1">
                  {pageActions.readLatest}
                  <span className="sr-only" data-id="element-7636" data-mp="34hk5gvnvnjvp">: {featured.title}</span>
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7637" data-mp="18dnu52gj8ub8" />
                </Link>

                {featuredProducts.length > 0 && <div className="mt-9 border-t border-ink/15 pt-6" data-id="element-7638" data-mp="1i9quc2rtqivj">
                    <p className="text-[14px] text-ink" data-id="element-7639" data-mp="1mdoqq5prlfkw">Genoemd in dit artikel</p>
                    <MentionedProducts products={featuredProducts} data-id="element-7640" data-mp="1rci8iac33sqv" />
                  </div>}
              </div>
            </article> : <p className="mt-10 text-[17px] text-muted" data-id="element-7641" data-mp="1w8grfien8ijw">{fill(copy.emptyCategory)}</p>}

          {rest.length > 0 && <ul className="mt-20 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-id="element-7642" data-mp="2614p0fdg6qjo">
              {rest.map(a => <li key={a.slug} className="h-full" data-id="element-7643" data-mp="2a3j3yodzx65o">
                  <Link to={articlePath(a)} state={linkState} className="group flex h-full flex-col" data-id="element-7644" data-mp="2f1ayzr9xt8e3">
                    {a.image ? <MediaView media={{
                image: a.image,
                alt: ''
              }} sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw" className="aspect-[3/2] w-full" data-id="element-7645" data-mp="3f0l5awvit1s" /> :
              // Geen beeld: een rustig vlak in dezelfde maat, zodat titels in de rij gelijk staan.
              <span aria-hidden="true" className="flex aspect-[3/2] w-full items-end bg-shell p-5" data-id="element-7646" data-mp="3lkvtmsmoi731">
                        <span className="type-name italic text-clay" data-id="element-7647" data-mp="m3xixorhc3or">{a.category}</span>
                      </span>}
                    <p className="mt-5 text-[13px] text-clay" data-id="element-7648" data-mp="i57zpfjrfrx7">{a.category}</p>
                    <h3 className="mt-2 type-title text-ink transition-colors duration-150 ease-soft group-hover:text-clay" data-id="element-7649" data-mp="daat0f9ydunh">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-7650" data-mp="15tzijgbgmxxb">{a.excerpt}</p>
                    <p className="mt-auto pt-4 text-[13px] text-muted" data-id="element-7651" data-mp="110y1b1ns6mak">
                      {a.date} · {a.readTime}
                      {a.productIds && a.productIds.length > 0 && <span className="text-clay" data-id="element-7652" data-mp="e4lh6uh3lr0b"> · {plural(a.productIds.length, 'product', 'producten')} uit de shop</span>}
                    </p>
                  </Link>
                </li>)}
            </ul>}
        </div>
      </section>

      <JournalPodcast data-id="element-7653" data-mp="4cz7j2kerzzo" />
    </>;
}